import { useState, useEffect, lazy, Suspense } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import VersionChecker from './components/VersionChecker';
import { CountryFormat } from './constants/countries';

// Lazy load non-critical pages for better performance
const UploadPage = lazy(() => import('./pages/UploadPage'));
const SelectCountryPage = lazy(() => import('./pages/SelectCountryPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PhotoGuidelinesPage = lazy(() => import('./pages/PhotoGuidelinesPage'));
const CountryRequirementsPage = lazy(() => import('./pages/CountryRequirementsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

type PageType = 'home' | 'upload' | 'select-country' | 'preview' | 'payment' | 'confirmation' | 'about' | 'help' | 'contact' | 'photo-guidelines' | 'country-requirements' | 'privacy-policy' | 'terms-of-service';

interface PageData {
  imageUrl?: string;
  country?: CountryFormat;
  file?: File;
}

function App() {
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [pageData, setPageData] = useState<PageData>({});

  // Détecter le retour depuis Stripe Payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Vérifier si on revient de Stripe (via page parameter ou session_id)
    const pageParam = urlParams.get('page');
    const sessionId = urlParams.get('session_id');

    if (pageParam === 'confirmation' || sessionId) {
      // Naviguer vers la page de confirmation
      setCurrentPage('confirmation');

      // Nettoyer l'URL sans recharger la page
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleNavigate = (page: string, data?: PageData) => {
    setCurrentPage(page as PageType);
    if (data) {
      setPageData(prevData => ({ ...prevData, ...data }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} />;
      case 'upload':
        return <UploadPage onNavigate={handleNavigate} />;
      case 'select-country':
        return <SelectCountryPage onNavigate={handleNavigate} imageUrl={pageData.imageUrl} file={pageData.file} />;
      case 'preview':
        return <PreviewPage onNavigate={handleNavigate} imageUrl={pageData.imageUrl} country={pageData.country} />;
      case 'payment':
        return <PaymentPage onNavigate={handleNavigate} imageUrl={pageData.imageUrl} country={pageData.country} />;
      case 'confirmation':
        return <ConfirmationPage onNavigate={handleNavigate} />;
      case 'about':
        return <AboutPage onNavigate={handleNavigate} />;
      case 'help':
        return <HelpPage onNavigate={handleNavigate} />;
      case 'contact':
        return <ContactPage onNavigate={handleNavigate} />;
      case 'photo-guidelines':
        return <PhotoGuidelinesPage onNavigate={handleNavigate} />;
      case 'country-requirements':
        return <CountryRequirementsPage onNavigate={handleNavigate} />;
      case 'privacy-policy':
        return <PrivacyPolicyPage onNavigate={handleNavigate} />;
      case 'terms-of-service':
        return <TermsOfServicePage onNavigate={handleNavigate} />;
      default:
        return <HomePage onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <VersionChecker />
      <Navbar currentPage={currentPage} onNavigate={handleNavigate} />
      <main className="flex-grow">
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        }>
          {renderPage()}
        </Suspense>
      </main>
      <Footer onNavigate={handleNavigate} />
    </div>
  );
}

export default App;
